import type { TaskDetailFragment } from '__generated__/graphql';

import { generatePath } from 'react-router-dom';

export function withSearchParams(url: string, searchParams: URLSearchParams) {
  const queryString = searchParams.toString();
  if (queryString.length === 0) {
    return url;
  }

  return `${url}?${queryString}`;
}

export enum AppPath {
  ProjectReportsList = '/projects/:projectId/reports/individual',
  ProjectReportsListCombined = '/projects/:projectId/reports/combined',
  ProjectReportsListLog = '/projects/:projectId/reports/logged',
  TemplateResponseView = '/projects/:projectId/reports/responses/:templateResponseId',
  TemplateResponseCombined = '/projects/:projectId/templates/:stepTemplateId/responses',
  ActivityList = '/projects/:projectId/activities',
  ActivityCreate = '/projects/:projectId/activities/create',
  ActivityCopy = '/projects/:projectId/activities/copy/:activityId',
  ActivityView = '/projects/:projectId/activities/:activityId',
  ActivityComment = '/projects/:projectId/activities/:activityId/comments',
  ActivityEdit = '/projects/:projectId/activities/edit/:activityId',
  ActivityAssignmentView = '/projects/:projectId/activities/:activityId/assignments/:assignmentId',
  BroadcastsList = '/projects/:projectId/broadcasts',
  BroadcastCreate = '/projects/:projectId/broadcasts/create',
  BroadcastEdit = '/projects/:projectId/broadcasts/edit/:broadcastId',
  BroadcastView = '/projects/:projectId/broadcasts/:broadcastId',
  BroadcastComment = '/projects/:projectId/broadcasts/:broadcastId/comments',
  NotAuthorized = '/unauthorized',
}

export const appChildPaths: {
  [AppPath.TemplateResponseCombined]: {
    List: string;
    StepResponsesView: string;
    StepGroupView: string;
  };
} = {
  [AppPath.TemplateResponseCombined]: {
    List: 'list',
    StepGroupView: 'groups/:stepGroupName/:questionNumber',
    StepResponsesView: 'groups/:stepGroupName/:questionNumber/steps/:stepId',
  },
};

export function makeChildPath<T extends keyof typeof appChildPaths>(
  path: T,
  childPath: keyof (typeof appChildPaths)[T]
) {
  return `${path}/${appChildPaths[path][childPath]}`;
}

export function getChildUrl<T extends keyof typeof appChildPaths>(
  path: T,
  childPath: keyof (typeof appChildPaths)[T],
  params?: Record<string, string | number | null>
) {
  return getUrlWithTokens(makeChildPath(path, childPath), params ?? {});
}

function getUrlWithTokens(path: string, params: Record<string, string | number | null>) {
  const stringParams = Object.fromEntries(
    Object.entries(params).map(([key, value]) => [key, value == null ? value : String(value)])
  );
  return generatePath(path, stringParams);
}

type UrlParam = string | number;
type WithProjectId = { projectId: UrlParam };
type WithActivityId = WithProjectId & { activityId: UrlParam };
type WithBroadcastId = WithProjectId & { broadcastId: UrlParam };

export function getProjectReportsListUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.ProjectReportsList, params);
}

export function getProjectReportsListCombinedUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.ProjectReportsListCombined, params);
}

export function getProjectReportsListLogUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.ProjectReportsListLog, params);
}

export function getTemplateResponseViewUrl(
  params: WithProjectId & { templateResponseId: UrlParam }
) {
  return getUrlWithTokens(AppPath.TemplateResponseView, params);
}

export function getTemplateResponseCombinedUrl(
  params: WithProjectId & { stepTemplateId: UrlParam }
) {
  return getUrlWithTokens(AppPath.TemplateResponseCombined, params);
}

export function getCombinedStepResponsesUrl(
  params: WithProjectId & {
    stepTemplateId: UrlParam;
    stepGroupName: string;
    stepId: UrlParam;
  }
) {
  return getChildUrl(AppPath.TemplateResponseCombined, 'StepResponsesView', params);
}

export function getCombinedStepGroupsUrl(
  params: WithProjectId & {
    stepTemplateId: UrlParam;
    stepGroupName?: string;
    questionNumber?: string;
  }
) {
  return getChildUrl(AppPath.TemplateResponseCombined, 'StepGroupView', params);
}

export function getActivityListUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.ActivityList, params);
}

export function getBroadcastListUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.BroadcastsList, params);
}

export function getBroadcastViewUrl(params: WithBroadcastId) {
  return getUrlWithTokens(AppPath.BroadcastView, params);
}

export function getBroadcastEditUrl(params: WithBroadcastId) {
  return getUrlWithTokens(AppPath.BroadcastEdit, params);
}

export function getActivityCreateUrl(params: WithProjectId) {
  return getUrlWithTokens(AppPath.ActivityCreate, params);
}

export function getActivityCopyUrl(params: WithActivityId) {
  return getUrlWithTokens(AppPath.ActivityCopy, params);
}

export function getActivityViewUrl(params: WithActivityId) {
  return getUrlWithTokens(AppPath.ActivityView, params);
}

export function getActivityEditUrl(params: WithActivityId) {
  return getUrlWithTokens(AppPath.ActivityEdit, params);
}

export function getActivityAssignmentViewUrl(params: WithActivityId & { assignmentId: UrlParam }) {
  return getUrlWithTokens(AppPath.ActivityAssignmentView, params);
}

export function buildActivityViewUrl(
  activity: Pick<TaskDetailFragment, 'id'>,
  campaignId: UrlParam
) {
  return getActivityViewUrl({
    projectId: Number(campaignId),
    activityId: activity.id,
  });
}

export function buildActivityAssignmentViewUrl(
  assignment: { id: number; activity: { id: number } },
  campaignId: UrlParam,
  activityId?: UrlParam
) {
  return getActivityAssignmentViewUrl({
    projectId: Number(campaignId),
    activityId: activityId ?? assignment.activity.id,
    assignmentId: assignment.id,
  });
}

export function buildTemplateResponseViewUrl(
  templateResponse: { id: string | number },
  campaignId: UrlParam
) {
  return getTemplateResponseViewUrl({
    projectId: Number(campaignId),
    templateResponseId: templateResponse.id,
  });
}
